import React, { useState, useMemo } from 'react';
import Seo from '../../components/Seo';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Phone, MapPin, Shield, Zap, CheckCircle2, Globe, Search } from 'lucide-react';
import { useCountries } from '../../hooks/useCountries';
import NumberCountryTable, { ResultsLine, matchesCountry } from '../../components/numbers/NumberCountryTable';

const LocalNumbers = () => {
  const { countries, loading } = useCountries();
  const localCountries = countries.filter(c => c.local);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCountries = useMemo(() => {
    if (!searchTerm) return localCountries;
    return localCountries.filter((c) => matchesCountry(c, searchTerm));
  }, [searchTerm, localCountries]);

  return (
    <div className="min-h-screen pt-24 pb-16">
      <Seo
        title="Local Phone Numbers with City Area Codes"
        description="Geographic local phone numbers tied to cities and regions in 50+ countries. Give customers a familiar local number to call."
        path="/phone-numbers/local"
        breadcrumbs={[{ name: 'Phone Numbers', path: '/phone-numbers/all' }, { name: 'Local Numbers', path: '/phone-numbers/local' }]}
      />
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6" style={{ color: '#0a1f2e' }}>
                Local Phone Numbers
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Establish local presence in over 50 countries with geographic numbers tied to specific cities and regions. Build trust with customers through familiar local numbers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
                  className="hover:opacity-90 font-semibold"
                  onClick={() => window.location.href = '/phone-numbers/all'}
                >
                  View All Coverage
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] rounded-lg p-6 sm:p-12 text-white">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>{localCountries.length}+</div>
                  <div className="text-sm mt-2">Countries</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>5000+</div>
                  <div className="text-sm mt-2">Area Codes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>99.99%</div>
                  <div className="text-sm mt-2">Uptime SLA</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>&lt;1min</div>
                  <div className="text-sm mt-2">Activation</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#0a1f2e' }}>
            Local Numbers Available in {localCountries.length} Countries
          </h2>
          <Card className="p-4 sm:p-6">
            {/* Search Filter */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by country name or prefix (e.g., Germany or +49)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-6 text-base"
                />
              </div>
              <div className="flex items-center justify-between gap-3 mt-3">
                <ResultsLine count={filteredCountries.length} total={localCountries.length} loading={loading} />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-sm text-[#c8ff00] hover:underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            </div>
            
            <NumberCountryTable countries={filteredCountries} group="local" />

            {filteredCountries.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No countries found matching "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-[#c8ff00] hover:underline"
                >
                  Clear search and show all countries
                </button>
              </div>
            )}
            
            <div className="mt-6 text-center">
              <Link to="/phone-numbers/all">
                <Button variant="outline">View Complete Coverage</Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Why Choose Local Numbers?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <MapPin className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Local Presence</h3>
              <p className="text-gray-600">Establish credibility with customers by providing familiar local area codes in their region.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Shield className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Full Compliance</h3>
              <p className="text-gray-600">All numbers meet local regulatory requirements and carrier guidelines.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Zap className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Instant Activation</h3>
              <p className="text-gray-600">Numbers are provisioned in less than a minute. Start receiving calls immediately.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <CheckCircle2 className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Number Porting</h3>
              <p className="text-gray-600">Port your existing local numbers to our platform with seamless migration.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Phone className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Voice & SMS</h3>
              <p className="text-gray-600">Support for both voice calls and SMS messaging on most local numbers.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Globe className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Global Reach</h3>
              <p className="text-gray-600">Available in {localCountries.length}+ countries with 5000+ area codes worldwide.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Perfect For
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>Local Businesses</h3>
              <p className="text-gray-600">Appear local to customers in multiple cities without physical offices</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>Support Centers</h3>
              <p className="text-gray-600">Provide local support numbers for better customer experience</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>Sales Teams</h3>
              <p className="text-gray-600">Increase answer rates with local caller ID for outbound calls</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to get local numbers?</h2>
            <p className="text-xl text-gray-300 mb-8">Start with instant access to local phone numbers in 50+ countries</p>
            <Button 
              size="lg"
              style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
              className="hover:opacity-90 font-semibold"
              onClick={() => window.location.href = '/contact'}
            >
              Get Started Now
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default LocalNumbers;